import axios from 'axios';
import { GetServerSideProps, NextPage } from 'next';
import Image from 'next/image';
import PageWrapper from '../components/PageWrapper';
import Typography from '../components/Typography';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { Button } from '../components/Button';
import { TfiPlus } from 'react-icons/tfi';
import styles from './contact-us.module.scss';
import { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { TextInput } from '../components/TextInput';
import { Checkbox } from '../components/Checkbox';
import SEO from '../components/SEO';

interface AboutData {
  _id: string;
  address: string;
  phone: string;
  aboutus: string;
  email?: string;
}

interface ContactUsPageProps {
  aboutData: any[];
}
interface FormValues {
  name: string;
  email: string;
  phone: string;
  company: string;
  message: string;
  file: File | null;
  privacy: boolean;
}

const ContactUsPage: NextPage<ContactUsPageProps> = ({ aboutData }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [fileName, setFileName] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validationSchema = Yup.object({
    name: Yup.string().required('Enter a full name'),
    email: Yup.string()
      .email('Invalid email')
      .required('Enter an email address like example@mysite.com.'),
    phone: Yup.string().matches(/^[0-9+\-\s()]+$/, 'Please enter a valid phone number'),
    company: Yup.string().max(100, 'Company name is too long'),
    message: Yup.string().required('Enter an answer.'),
    privacy: Yup.boolean()
      .oneOf([true], 'You must agree to the privacy policy.')
      .required('You must agree to the privacy policy.'),
  });

  const initialValues: FormValues = {
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
    file: null,
    privacy: false,
  };

  const handleSubmit = async (values: FormValues, { resetForm }: any): Promise<void> => {
    const formData = new FormData();
    formData.append('name', values.name);
    formData.append('email', values.email);
    if (values.phone) formData.append('phone', values.phone);
    if (values.company) formData.append('company', values.company);
    formData.append('message', values.message);

    if (values.file) {
      formData.append('file', values.file);
    }

    try {
      setLoading(true);
      const response = await axios.post(
        `${process.env.NEXT_PUBLIC_REACT_APP_BASE_URL1}/contacts`,
        formData
      );
      toast.success('Your message has been sent successfully.');

      // Reset form and file state
      setFileName('');
      resetForm();
      setLoading(false);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || 'Something went wrong. Please try again.'
      );
      setLoading(false);
    }
  };
  const handleFileDelete = (setFieldValue: (field: string, value: File | null) => void): void => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setFileName('');
    setFieldValue('file', null);
  };

  return (
    <>
      <SEO
        title="Contact Us"
        description="Contact EPEN Ltd. for video plugin development, software solutions & technical support. Our global team is ready to help. Get in touch today!"
        keywords="contact, epen, support, video plugin development"
      />
      <PageWrapper
        dateText="Contact us"
        title="Contact Us"
        titleText="We'd love to hear from you"
        introText="We have offices and teams all around the world."
        aboutData={aboutData}
        additionalHeaderContent={
          <div className={styles.contactInfo}>
            <div className={styles.contactInfoItem}>
              <div className={styles.contactInfoItemIcon}>
                <Image src="/contact-email.svg" alt="Contact EPEN Ltd. via Email" width={48} height={48} />
              </div>

              <div className={styles.contactInfoItemContent}>
                <Typography variant="text-lg" color="grayDark-50">
                  Email
                </Typography>
                <Typography variant="text-md" color="grayDark-400">
                  Our friendly team is here to help.
                </Typography>
              </div>

              <a href="mailto:epen@epen.dev" style={{ textDecoration: 'none' }}>
                <Typography variant="text-md" color="grayDark-300" as="span">
                  epen@epen.dev
                </Typography>
              </a>
            </div>

            <div className={styles.contactInfoItem}>
              <div className={styles.contactInfoItemIcon}>
                <Image
                  src="/contact-location.svg"
                  alt="EPEN Ltd. Office Location in Sofia, Bulgaria"
                  width={48}
                  height={48}
                />
              </div>

              <div className={styles.contactInfoItemContent}>
                <Typography variant="text-lg" color="grayDark-50">
                  Office
                </Typography>
                <Typography variant="text-md" color="grayDark-400">
                  Come say hello at our office HQ.
                </Typography>
              </div>

              <Typography variant="text-md" color="grayDark-300">
                Racho Dimchev” Str., 1st floor, ap.1, Sofia, 1000, Bulgaria
              </Typography>
            </div>

            <div className={styles.contactInfoItem}>
              <div className={styles.contactInfoItemIcon}>
                <Image src="/contact-phone.svg" alt="Contact EPEN Ltd. via Phone" width={48} height={48} />
              </div>

              <div className={styles.contactInfoItemContent}>
                <Typography variant="text-lg" color="grayDark-50">
                  Phone
                </Typography>
                <Typography variant="text-md" color="grayDark-400">
                  Mon-Fri from 8am to 5pm.
                </Typography>
              </div>

              <a href="tel:+35924372736" style={{ textDecoration: 'none' }}>
                <Typography variant="text-md" color="grayDark-300" as="span">
                  +35924372736
                </Typography>
              </a>
            </div>
          </div>
        }
      >
        <section className={styles.contentSection}>
          <div className={styles.title}>
            <Typography variant="text-md" color="grayDark-300">
              Contact us
            </Typography>
            <Typography variant="display-md" color="grayDark-50" style={{ marginBottom: '8px' }}>
              Get in touch
            </Typography>
            <Typography variant="text-xl" color="grayDark-400">
              We’d love to hear from you. Please fill out this form.
            </Typography>
          </div>

          <div className={styles.formContainer}>
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              validateOnChange={true}
              validateOnBlur={true}
            >
              {({ errors, touched, setFieldValue, values, setFieldTouched }) => (
                <Form className={styles.form}>
                  <div className={styles.formRowFullWidth}>
                    <TextInput
                      id="name"
                      name="name"
                      type="text"
                      label="Full name"
                      placeholder="Enter your full name"
                      fullWidth
                      required
                      autoFocus
                      value={values.name}
                      onChange={(e: any) => setFieldValue('name', e.target.value)}
                      onBlur={() => setFieldTouched('name', true)}
                      error={touched.name && errors.name ? errors.name : undefined}
                    />
                  </div>

                  <div className={styles.formRowFullWidth}>
                    <TextInput
                      id="email"
                      name="email"
                      type="email"
                      label="Email"
                      placeholder="Enter your email"
                      fullWidth
                      required
                      value={values.email}
                      onChange={(e: any) => setFieldValue('email', e.target.value)}
                      onBlur={() => setFieldTouched('email', true)}
                      inputType="email"
                      error={touched.email && errors.email ? errors.email : undefined}
                    />
                  </div>

                  <div className={styles.formRowFullWidth}>
                    <TextInput
                      id="phone"
                      name="phone"
                      type="tel"
                      label="Phone number"
                      placeholder="Enter your phone number"
                      fullWidth
                      inputType="phone"
                      value={values.phone}
                      onChange={(e: any) => setFieldValue('phone', e.target.value)}
                      onBlur={() => setFieldTouched('phone', true)}
                      error={touched.phone && errors.phone ? errors.phone : undefined}
                    />
                  </div>

                  <div className={styles.formRowFullWidth}>
                    <TextInput
                      id="company"
                      name="company"
                      type="text"
                      label="Company name"
                      placeholder="Enter your company name"
                      fullWidth
                      value={values.company}
                      onChange={(e: any) => setFieldValue('company', e.target.value)}
                      onBlur={() => setFieldTouched('company', true)}
                      error={touched.company && errors.company ? errors.company : undefined}
                    />
                  </div>

                  <div className={styles.formRowFullWidth}>
                    <TextInput
                      id="message"
                      name="message"
                      label="Message"
                      placeholder="Leave us a message..."
                      fullWidth
                      required
                      multiline
                      rows={4}
                      value={values.message}
                      onChange={(e: any) => setFieldValue('message', e.target.value)}
                      onBlur={() => setFieldTouched('message', true)}
                      error={touched.message && errors.message ? errors.message : undefined}
                    />
                  </div>

                  {fileName && (
                    <div className={styles.fileContainer}>
                      <div className={styles.fileInfo}>
                        <span className={styles.fileLabel}>Attached file:</span>
                        <span className={styles.fileName} title={fileName}>
                          {fileName}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleFileDelete(setFieldValue);
                        }}
                        aria-label={`Remove ${fileName}`}
                        className={styles.deleteBtn}
                      >
                        <Image src="/deleteicon.png" alt="Delete file" width={24} height={24} />
                      </button>
                    </div>
                  )}

                  <div className={styles.formRow}>
                    <Checkbox
                      id="privacy"
                      name="privacy"
                      label={
                        <>
                          You agree to our friendly{' '}
                          <Typography
                            variant="text-md"
                            as="span"
                            color="brand-500"
                            style={{
                              cursor: 'pointer',
                            }}
                            onClick={() => window.open('/privacy-policy', '_blank')}
                          >
                            privacy policy
                          </Typography>
                        </>
                      }
                      required
                      checked={values.privacy}
                      onChange={(checked: boolean) => setFieldValue('privacy', checked)}
                      onBlur={() => setFieldTouched('privacy', true)}
                      error={touched.privacy && errors.privacy ? errors.privacy : undefined}
                    />
                  </div>

                  <div className={styles.buttonRow}>
                    <Button
                      type="submit"
                      fullWidth
                      disabled={loading}
                      label={loading ? 'Sending...' : 'Send message'}
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      fullWidth
                      onClick={() => {
                        fileInputRef.current?.click();
                      }}
                      icon={
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path
                            d="M8 12L8 4M4 8L8 4L12 8"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      }
                      iconPosition="right"
                      label="Upload File"
                    />

                    <input
                      type="file"
                      accept="image/*, application/pdf, .doc, .docx"
                      className={styles.filess}
                      ref={fileInputRef}
                      onChange={(e) => {
                        if (e.currentTarget.files) {
                          setFileName(e.currentTarget.files[0]?.name || '');
                          setFieldValue('file', e.currentTarget.files[0]);
                        }
                      }}
                      style={{ display: 'none' }}
                      aria-label="File input"
                    />
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </section>
      </PageWrapper>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async () => {
  let aboutData = [];

  try {
    const aboutResponse = await axios.get(
      `${process.env.NEXT_PUBLIC_REACT_APP_BASE_URL1}/about-us`
    );
    aboutData = aboutResponse.data.data || [];
  } catch (error) {
    console.error('Error fetching about-us data for contact-us page:', error);
  }

  return {
    props: {
      aboutData,
    },
  };
};

export default ContactUsPage;
